import { type FC, memo, useCallback, useLayoutEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import Image from '../../../../../shared/components/Image';
import {
  useCampaignDataAttributes,
  useCampaignActions,
  useCampaigns,
  useIsHeaderCollapsed,
  useCampaignCardScale,
  useCampaignTypeById,
} from 'features/campaigns';
import { useJoinCampaign } from '../../../hooks/data/useJoinCampaign';
import { useJoinInBtnStyles } from 'features/campaigns/hooks';
import { testIds } from 'core/testIds';
import type { CampaignTypes } from '../../../types/campaign.types';

import styles from './JoinInButton.module.css';
import { clsx } from 'clsx';

type JoinInButtonProps = {
  campaignId: string;
  variant?: 'panel' | 'card';
};

type JoinInButtonContentProps = JoinInButtonProps & {
  campaignType: CampaignTypes;
};

// The selected id can point at a campaign that is no longer (or not yet) in the
// store — e.g. it dropped out of a refetch, or the client sent a campaignID
// before the list loaded. Without a type there is no config to join with, so
// render nothing instead of letting useJoinCampaign throw.
const JoinInButton: FC<JoinInButtonProps> = ({ campaignId, variant = 'card' }) => {
  const campaignType = useCampaignTypeById(campaignId);

  if (!campaignType) return null;

  return <JoinInButtonContent campaignId={campaignId} variant={variant} campaignType={campaignType} />;
};

const JoinInButtonContent: FC<JoinInButtonContentProps> = ({ campaignId, variant = 'card', campaignType }) => {
  const { t } = useTranslation();
  const joinInText = t('campaigns.join_in', 'Join In');
  const joinedText = t('campaigns.joined', 'Joined');

  const atrributes = useCampaignDataAttributes({
    campaignType,
    additionalProps: {
      'data-variant': variant
    }
  });

  const targetCampaignId = campaignId;

  const btnStyles = useJoinInBtnStyles(targetCampaignId);

  // Get campaign/tournament data from unified store
  const items = useCampaigns(campaignType);

  const isJoined = items.find(c => c.id === targetCampaignId)?.joined ?? false;

  const { updateCampaign } = useCampaignActions();

  const { joinCampaign, isJoiningCampaign } = useJoinCampaign(campaignType);
  const isJoining = isJoiningCampaign;

  // Track initial joined state to only animate on transition
  const initialJoined = useRef(isJoined).current;

   // Handle join button click
  const handleJoinClick = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!targetCampaignId || isJoining || isJoined) {
      return;
    }

    // Optimistic update
    updateCampaign(targetCampaignId, { joined: true });

    try {
      const result = await joinCampaign(targetCampaignId);
      const joinResult = result.find((r: { id: string; joined: boolean }) => r.id === targetCampaignId);
      if (!joinResult?.joined) {
        updateCampaign(targetCampaignId, { joined: false });
      }
    } catch (error) {
      console.error('Failed to join:', error);
      updateCampaign(targetCampaignId, { joined: false });
    }
  }, [targetCampaignId, isJoining, isJoined, joinCampaign, updateCampaign]);

  const showAnimation = isJoined && !initialJoined;
  const joinText = isJoined ? joinedText : joinInText;

  const shouldScale = variant === 'card';
  const isCollapsed = useIsHeaderCollapsed();

  const btnRef = useRef<HTMLDivElement>(null);
  const [maxTextWidth, setMaxTextWidth] = useState<number | undefined>(undefined);

  // Track card width via ResizeObserver so maxTextWidth stays accurate
  // after device rotation / viewport resize.
  useLayoutEffect(() => {
    if (!shouldScale || !btnRef.current) return;

    const update = () => {
      if (!btnRef.current) return;
      const card = btnRef.current.offsetParent as HTMLElement;
      if (!card || card.clientWidth === 0) return;
      setMaxTextWidth(Math.floor(card.clientWidth * 0.2));
    };

    update();

    const resizeObserver = new ResizeObserver(update);
    
    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            requestAnimationFrame(() => {
              requestAnimationFrame(update);
            });
          }
        });
      },
      { threshold: 0.01 },
    );

    if (btnRef.current) {
      const card = btnRef.current.offsetParent as HTMLElement;
      if (card) resizeObserver.observe(card);
      intersectionObserver.observe(btnRef.current);
    }

    return () => {
      resizeObserver.disconnect();
      intersectionObserver.disconnect();
    };
  }, [shouldScale, joinText]);

  const { titleRef, scale, scaledWidth } = useCampaignCardScale(joinText, {
    maxWidth: maxTextWidth,
    recalculateKey: `${isCollapsed}-${joinText}`,
    disabled: !shouldScale || !maxTextWidth,
  });

  return (
    <div
      ref={btnRef}
      className={clsx(styles.joinBtn, isJoined && styles.joined, showAnimation && styles.animate)}
      style={btnStyles}
      {...atrributes}
      onClick={handleJoinClick}
      data-testid={testIds.campaigns.joinInButton(campaignType, variant)}
    >
      <Image
        src={`/campaigns/icons/${isJoined ? 'Joined' : 'JoinInPlus'}`}
        manualAffix=".svg"
        className={styles.joinBtnIcon}
        {...atrributes}
      />
      <span
        ref={titleRef}
        className={styles.joinBtnText}
        {...atrributes}
        style={shouldScale && scale < 1 ? {
          transform: `scale(${scale})`,
          transformOrigin: 'left center',
          width: scaledWidth,
        } : undefined}
      >
        {joinText}
      </span>
    </div>
  );
};

export default memo(JoinInButton);
